import { Component, inject, OnDestroy, OnInit, signal, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterModule, RouterOutlet, NavigationEnd } from '@angular/router';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { StyleClassModule } from 'primeng/styleclass';
import { RippleModule } from 'primeng/ripple';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { far } from '@fortawesome/pro-regular-svg-icons';
import { fas } from '@fortawesome/pro-solid-svg-icons';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { filter, fromEvent, Subject, takeUntil } from 'rxjs';
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  IdTokenClaims,
  InteractionStatus,
  InteractionType,
  PopupRequest,
  PromptValue,
  RedirectRequest,
} from '@azure/msal-browser';

import { ToastModule } from 'primeng/toast';
import { Store } from '@ngrx/store';
import { SwUpdate } from '@angular/service-worker';
import { ConfirmPopup, ConfirmPopupModule } from 'primeng/confirmpopup';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { InfoMessagesService, ShellComponent, SignalrService } from '@workspace-app/shared';
import { GlobalStateActions } from './state/global';
import { PdfViewerComponent } from './shared/components/pdf-viewer/pdf-viewer.component';
import { DocxViewerComponent } from './shared/components/docx-viewer/docx-viewer.component';
import { clearAuthRedirects } from './auth/auth-redirect-loop-breaker';
import { environment } from '../environments/environment';

type IdTokenClaimsWithPolicyId = IdTokenClaims & {
  acr?: string;
  tfp?: string;
};

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AvatarModule,
    BadgeModule,
    RippleModule,
    RouterOutlet,
    RouterModule,
    ShellComponent,
    StyleClassModule,
    ToastModule,
    ConfirmPopupModule,
    ConfirmDialog,
    PdfViewerComponent,
    DocxViewerComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  private iconLibrary = inject(FaIconLibrary);
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
  private store = inject(Store);
  private swUpdate = inject(SwUpdate);
  private infoMessageService = inject(InfoMessagesService);
  private signalrService = inject(SignalrService);
  private router = inject(Router);

  // add title
  title = 'QCT360';
  loginDisplay = false;
  reloadNeeded = signal(false);
  isInitializing = signal(true);
  private readonly _destroying$ = new Subject<void>();

  @ViewChild(ConfirmPopup) confirmPopup!: ConfirmPopup;

  constructor() {
    // Defer icon loading to improve initial load
    setTimeout(() => {
      this.iconLibrary.addIconPacks(far);
      this.iconLibrary.addIconPacks(fas);
    }, 100);

    fromEvent(window, 'online').subscribe(() => {
      this.infoMessageService.showInfoMessage(
        'The application has restored the connection to the server',
        'Application online'
      );
      this.store.dispatch(GlobalStateActions.applicationIsOnline());
    });
    fromEvent(window, 'offline').subscribe(() => {
      this.infoMessageService.showInfoMessage(
        'The application has lost the connection to the server',
        'Application offline'
      );
      this.store.dispatch(GlobalStateActions.applicationIsOffline());
    });
    if (!navigator.onLine) {
      this.infoMessageService.showInfoMessage('The application has no connection to the server', 'Application offline');
      this.store.dispatch(GlobalStateActions.applicationIsOffline());
    }
    if (this.swUpdate.isEnabled) {
      // Service workers are enabled and can be used
      console.info('Service worker updates are enabled');
      this.swUpdate.versionUpdates.subscribe(async (e) => {
        switch (e.type) {
          case 'VERSION_DETECTED': {
            console.info('Downloading new app version:', e.version.appData);
            break;
          }
          case 'VERSION_READY': {
            console.info('New version ready:', e.currentVersion.appData, '->', e.latestVersion.appData);

            // Activate the new service worker in the background
            try {
              await this.swUpdate.activateUpdate();
              console.info('✓ New version activated');
            } catch (err) {
              console.error('Failed to activate update:', err);
            }

            // Clear all service worker caches (including critical dataGroups like constants)
            if ('caches' in window) {
              try {
                const cacheNames = await caches.keys();
                await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
                console.info('✓ All service worker caches cleared:', cacheNames);
              } catch (err) {
                console.error('Failed to clear caches:', err);
              }
            }

            // Show persistent message - user decides when to reload
            this.infoMessageService.showInfoMessage(
              'A new version is ready with updated data and constants. Please save your work and reload when ready.',
              'Update Available'
            );
            this.reloadNeeded.set(true);
            break;
          }
          case 'VERSION_INSTALLATION_FAILED': {
            console.error(`Failed to install ${e.version.appData}:`, e.error);
            break;
          }
        }
      });
    } else {
      // Service workers are enabled but may not be registered yet
      console.info('Service worker updates are not yet enabled');
    }
  }

  ngOnInit() {
    // CRITICAL: Must be first line to handle OAuth redirects properly
    this.msalService.handleRedirectObservable().subscribe({
      next: (result) => {
        console.log('Redirect handled successfully', result);
        if (result) {
          // A redirect that came back with a token was no loop; let the breaker start counting afresh.
          clearAuthRedirects();
        }
      },
      error: (error) => {
        console.error('Error handling redirect', error);
        // Still allow app to initialize even if redirect fails
        this.isInitializing.set(false);
      },
    });

    this.signalrService.init();

    // Subscribe to router events to hide shell for public routes
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this._destroying$)
      )
      .subscribe((event) => {
        const navigationEvent = event as NavigationEnd;
        // Hide shell for public routes even if user is logged in
        if (navigationEvent.urlAfterRedirects.startsWith('/public')) {
          this.loginDisplay = false;
        } else {
          this.setLoginDisplay();
        }
      });

    this.msalBroadcastService.msalSubject$
      .pipe(filter((msg: EventMessage) => msg.eventType === EventType.ACTIVE_ACCOUNT_CHANGED))
      .subscribe(() => {
        if (this.msalService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        } else {
          this.setLoginDisplay();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.isInitializing.set(false);
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        const idtoken = payload.idTokenClaims as IdTokenClaimsWithPolicyId;

        if (
          idtoken.acr === environment.b2cPolicies.names.signIn ||
          idtoken.tfp === environment.b2cPolicies.names.signIn
        ) {
          this.msalService.instance.setActiveAccount(payload.account);
        }

        /**
         * Below we are checking if the user is returning from the reset password flow.
         * If so, we will ask the user to reauthenticate with their new password.
         * If you do not want this behavior and prefer your users to stay signed in instead,
         * you can replace the code below with the same pattern used for handling the return from
         * profile edit flow (see above ln. 74-92).
         */
        if (
          idtoken.acr === environment.b2cPolicies.names.resetPassword ||
          idtoken.tfp === environment.b2cPolicies.names.resetPassword
        ) {
          const signInFlowRequest: RedirectRequest | PopupRequest = {
            authority: environment.b2cPolicies.authorities.signIn.authority,
            scopes: [...environment.apiConfig.scopes],
            prompt: PromptValue.LOGIN, // force user to reauthenticate with their new password
          };

          this.login(signInFlowRequest);
        }

        return result;
      });

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        // Check for forgot password error
        // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
        if (result.error && result.error.message.indexOf('AADB2C90118') > -1) {
          const resetPasswordFlowRequest: RedirectRequest | PopupRequest = {
            authority: environment.b2cPolicies.authorities.resetPassword.authority,
            scopes: [],
          };

          this.login(resetPasswordFlowRequest);
        }
      });
  }

  setLoginDisplay() {
    this.loginDisplay = this.msalService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      const accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  login(userFlowRequest?: RedirectRequest | PopupRequest) {
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      if (this.msalGuardConfig.authRequest) {
        this.msalService
          .loginPopup({
            ...this.msalGuardConfig.authRequest,
            ...userFlowRequest,
          } as PopupRequest)
          .subscribe((response: AuthenticationResult) => {
            this.msalService.instance.setActiveAccount(response.account);
          });
      } else {
        this.msalService.loginPopup(userFlowRequest).subscribe((response: AuthenticationResult) => {
          this.msalService.instance.setActiveAccount(response.account);
        });
      }
    } else {
      if (this.msalGuardConfig.authRequest) {
        this.msalService.loginRedirect({
          ...this.msalGuardConfig.authRequest,
          ...userFlowRequest,
        } as RedirectRequest);
      } else {
        this.msalService.loginRedirect(userFlowRequest);
      }
    }
  }

  reloadApplication(): void {
    window.location.reload();
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
